import { Text, DataDisplay, SectionSubHeading } from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";

import { CheckScoreTag } from "../check-score-tag/CheckScoreTag";
import { CheckResult } from "../types";

type ReportSummaryProps = {
  checkResults: CheckResult[];
};

export function ReportSummary({ checkResults }: ReportSummaryProps) {
  const { t } = useTranslation();
  const { includeUmbrellaChecksSectionsInPdfReportSummary } = useFlags();

  const umbrellaChecks = checkResults.filter((elem) => {
    return elem.child_checks && elem.child_checks.length > 0;
  });

  const listOfChecks = (checkResults: CheckResult[]) => {
    return (
      <DataDisplay
        hideHeaderBorder
        columns={[
          {
            isRowHeader: true,
            key: "type",
            textValue: "Check type",
            width: "50%",
            render({ type }) {
              return (
                <Text style="pdf/row-header" testID="report-summary-check-type">
                  {type}
                </Text>
              );
            },
          },
          {
            key: "status",
            textValue: "Check status",
            render({ completedAt, status }) {
              if (status === "Complete") {
                return t(`Completed {{date}}`, {
                  date: formatDate(completedAt, "PP"),
                });
              }
              return status;
            },
          },
          {
            key: "score",
            textValue: "Check score",
            render({ score, subScore }) {
              return <CheckScoreTag score={score} subScore={subScore} />;
            },
          },
        ]}
        rows={checkResults.map((checkResult) => ({
          key: checkResult.id,
          type: checkResult.type_display,
          status: checkResult.status_display,
          completedAt: checkResult.completed_at,
          score: checkResult.score,
          subScore: checkResult.sub_score,
        }))}
      />
    );
  };

  return (
    <>
      {listOfChecks(checkResults)}

      {includeUmbrellaChecksSectionsInPdfReportSummary &&
        umbrellaChecks.map((parentCheck, umbrellaCheckIndex) => {
          return (
            <div
              key={umbrellaCheckIndex}
              className={styles.umbrellaParentHeading}
            >
              <SectionSubHeading title={parentCheck.type_display} />
              <div className={styles.childChecksList}>
                {parentCheck.child_checks &&
                  listOfChecks(parentCheck.child_checks)}
              </div>
            </div>
          );
        })}
    </>
  );
}

const styles = {
  umbrellaParentHeading: css({
    paddingTop: "var(--space-4)",
  }),
  childChecksList: css({
    marginLeft: "var(--space-3)",
    marginRight: "var(--space-3)",
    marginTop: "calc(var(--space-10)*-1)", // negative margin - handles the fact there's a blank THEAD element on the table and top margin
  }),
};
